import { useState, useEffect } from 'react';
import { useCashSession } from '../../hooks/useCashSession';
import cashRegisterService from '../../services/cashRegisterService';
import CashSessionOpen from '../../components/cash/CashSessionOpen';
import CashSessionActive from '../../components/cash/CashSessionActive';
import CashSessionClose from '../../components/cash/CashSessionClose';

const CashRegister = () => {
  const [cashRegisters, setCashRegisters] = useState([]);
  const [selectedRegister, setSelectedRegister] = useState(null);
  const [loadingRegisters, setLoadingRegisters] = useState(true);
  const [showOpenModal, setShowOpenModal] = useState(false);
  const [showCloseModal, setShowCloseModal] = useState(false);

  const { 
    activeSession, 
    loading: sessionLoading, 
    hasActiveSession,
    openSession,
    closeSession,
    refresh 
  } = useCashSession();

  // Cargar cajas registradoras al montar
  useEffect(() => {
    loadCashRegisters();
  }, []);

  const loadCashRegisters = async () => {
    try {
      setLoadingRegisters(true);
      const response = await cashRegisterService.getAll();
      
      if (response.success && response.data) {
        setCashRegisters(response.data);
        
        // Si hay una sesión activa, seleccionar esa caja
        if (activeSession) {
          const activeCashRegister = response.data.find(
            cr => cr.id === activeSession.cash_register_id
          );
          setSelectedRegister(activeCashRegister);
        } else if (response.data.length > 0) {
          // Si no hay sesión activa, seleccionar la primera caja
          setSelectedRegister(response.data[0]);
        }
      }
    } catch (error) {
      console.error('Error loading cash registers:', error);
    } finally {
      setLoadingRegisters(false);
    }
  };

  const handleOpenSession = async (data) => {
    const result = await openSession({
      ...data,
      cash_register_id: selectedRegister.id,
    });

    if (result.success) {
      setShowOpenModal(false);
      refresh();
    }

    return result;
  };

  const handleCloseSession = async (data) => {
    const result = await closeSession(activeSession.id, data);

    if (result.success) {
      setShowCloseModal(false);
      refresh();
    }

    return result;
  };

  if (loadingRegisters || sessionLoading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '400px' }}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Cargando...</span>
        </div>
      </div>
    );
  }

  if (cashRegisters.length === 0) {
    return (
      <div className="container mt-4">
        <div className="alert alert-warning">
          <h5>No hay cajas registradoras configuradas</h5>
          <p>Por favor, contacta al administrador para crear una caja registradora.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid mt-4">
      <div className="row mb-4">
        <div className="col-12">
          <h2 className="mb-3">
            <i className="bi bi-cash-register me-2"></i>
            Control de Caja
          </h2>
        </div>
      </div>

      {/* Selector de Caja */}
      <div className="row mb-4">
        <div className="col-md-6">
          <div className="card">
            <div className="card-body">
              <label className="form-label fw-bold">Caja Registradora</label>
              <select
                className="form-select"
                value={selectedRegister?.id || ''}
                onChange={(e) => {
                  const register = cashRegisters.find(
                    cr => cr.id === parseInt(e.target.value)
                  );
                  setSelectedRegister(register);
                }}
                disabled={hasActiveSession}
              >
                {cashRegisters.map(register => (
                  <option key={register.id} value={register.id}>
                    {register.nombre} - {register.punto_venta.nombre}
                    {register.tiene_sesion_abierta && ' (Abierta)'}
                  </option>
                ))}
              </select>
              {hasActiveSession && (
                <small className="text-muted">
                  No puedes cambiar de caja mientras haya una sesión abierta
                </small>
              )}
            </div>
          </div>
        </div>

        <div className="col-md-6">
          <div className="card">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <span className="fw-bold">Estado: </span>
                  {hasActiveSession ? (
                    <span className="badge bg-success">Sesión Abierta</span>
                  ) : (
                    <span className="badge bg-secondary">Sin Sesión</span>
                  )}
                </div>
                
                <div>
                  {!hasActiveSession ? (
                    <button
                      className="btn btn-primary"
                      onClick={() => setShowOpenModal(true)}
                      disabled={!selectedRegister}
                    >
                      <i className="bi bi-door-open me-2"></i>
                      Abrir Caja
                    </button>
                  ) : (
                    <button
                      className="btn btn-danger"
                      onClick={() => setShowCloseModal(true)}
                    >
                      <i className="bi bi-door-closed me-2"></i>
                      Cerrar Caja
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Sesión Activa */}
      {hasActiveSession && (
        <div className="row">
          <div className="col-12">
            <CashSessionActive 
              session={activeSession} 
              onRefresh={refresh}
            />
          </div>
        </div>
      )}

      {/* Sin Sesión */}
      {!hasActiveSession && (
        <div className="row">
          <div className="col-12">
            <div className="alert alert-info">
              <i className="bi bi-info-circle me-2"></i>
              No hay una sesión de caja abierta. Haz clic en "Abrir Caja" para comenzar.
            </div>
          </div>
        </div>
      )}

      {/* Modales */}
      {showOpenModal && (
        <CashSessionOpen
          show={showOpenModal}
          onClose={() => setShowOpenModal(false)}
          onSubmit={handleOpenSession}
          cashRegister={selectedRegister}
        />
      )}

      {showCloseModal && activeSession && (
        <CashSessionClose
          show={showCloseModal}
          onClose={() => setShowCloseModal(false)}
          onSubmit={handleCloseSession}
          session={activeSession}
        />
      )}
    </div>
  );
};

export default CashRegister;